import { useCallback, DragEvent } from "react";

import { clsx } from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { useReactFlow, isNode, isEdge, Node, Edge } from "reactflow";

import { ToolbarButton } from "Components/shared/ToolbarButton/ToolbarButton";
import {
    selectSelection,
    selectConnectedElements,
    copySelection,
    removeSelection,
    updateMultipleNodePos,
    resetCanvasAnchorPoint,
} from "Services/state/journeys/JourneyStateSlice";
import { NodeType } from "Types/Journey";

import { recenterCanvas, reorganizeNodes } from "../../shared/FlowHelpers";

import styles from "./Toolbar.module.scss";

const onDragStart = (event: DragEvent) => {
    event.dataTransfer.setData("application/reactflow", NodeType.StickyNote);
    event.dataTransfer.effectAllowed = "move";
};

export function Toolbar(): JSX.Element {
    const dispatch = useDispatch();
    const selection = useSelector(selectSelection);

    const elements = useSelector(selectConnectedElements) as (Node | Edge)[];
    const reactFlowInstance = useReactFlow();

    const zoomIn = useCallback(() => reactFlowInstance?.zoomIn(), [reactFlowInstance]);
    const zoomOut = useCallback(() => reactFlowInstance?.zoomOut(), [reactFlowInstance]);

    function isDisabled() {
        if (selection == null) {
            return true;
        }
        // trigger node cannot be copied/deleted
        return selection.length == 1 && selection[0].type == NodeType.Trigger;
    }

    const duplicateSelection = useCallback(() => {
        if (selection) {
            dispatch(copySelection());
        }
    }, [dispatch, selection]);

    const deleteSelection = useCallback(() => {
        if (selection) {
            dispatch(removeSelection());
        }
    }, [dispatch, selection]);

    const organizeConnectedNodes = useCallback(() => {
        dispatch(resetCanvasAnchorPoint());
        const nodes = elements.filter((el) => isNode(el)) as Node[];
        const edges = elements.filter((el) => isEdge(el)) as Edge[];
        const { nodes: updatedNodeSelection, anchorPoint } = reorganizeNodes(nodes, edges);
        dispatch(updateMultipleNodePos(updatedNodeSelection));
        recenterCanvas(anchorPoint, null, reactFlowInstance);
    }, [dispatch, elements, reactFlowInstance]);

    return (
        <div className={clsx("flex-row align-center", styles.toolbar)}>
            <ToolbarButton icon="zoom-in" tooltip="Zoom In" onClick={zoomIn} dataTestId="zoom-in" />
            <ToolbarButton icon="zoom-out" tooltip="Zoom Out" onClick={zoomOut} dataTestId="zoom-out" />
            <ToolbarButton
                icon="wand"
                tooltip="Organize Journey Layout"
                onClick={organizeConnectedNodes}
                dataTestId="reorganize"
            />
            <div onDragStart={onDragStart} draggable>
                <ToolbarButton icon="sticky-note" tooltip="Drag New Note" color="gray-5" dataTestId="add-sticky-note" />
            </div>
            <ToolbarButton
                icon="copy-node"
                tooltip="Copy"
                disabled={isDisabled()}
                onClick={duplicateSelection}
                dataTestId="copy-selection"
            />
            <ToolbarButton
                icon="delete-node"
                tooltip="Delete"
                disabled={isDisabled()}
                onClick={deleteSelection}
                dataTestId="delete-selection"
            />
        </div>
    );
}
